'use client'

import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react'
import React from 'react'

import Image from 'next/image'
import { LocationIcon } from '@icons/location-icon'
import { Link as ScrollLink } from 'react-scroll'
import Link from 'next/link'
import { useResponsive } from '@common/hooks'
import { MainContainer } from '.'

interface Outlets {
  location?: string
  stores: string[]
}

const JMLOutlets: Outlets[] = [
  {
    location: '',
    stores: ['Causeway Point #03-19']
  }
]

const OGOutlets: Outlets[] = [
  {
    location: '',
    stores: ['OG People Park #Level 5', 'OG Albert #Level 3']
  }
]

const GuardianOutlets: Outlets[] = [
  {
    location: 'Guardian - Central',
    stores: [
      '704 Ang Mo Kio Ave 8 #01-2559C',
      'Somerset 313 #B3-13/15 ',
      'Holland Shopping Centre #01-10/11',
      'Blk 190 Toa Payoh Lorong 6 #01-578',
      'Citylink Mall #B1-67',
      'Takashimaya #B2-15 ',
      'Great World #B1-122/123/124/125',
      'New Tanglin Mall #01-121/122',
      'Plaza Singapura #B2-15',
      'New Suntec City #02-705/707',
      'AMK HUB #B2-01/02',
      'Clarke Quay Central #01-55/56/57',
      'Raffles Place MRT #B1-37-40/43-45',
      'The Woodleigh Mall #B1-36',
      'Novena Hospital #01-08/09/10/11',
      'Suntec City #B1-154/156',
      'Paragon #B1-20/24',
      'Tiong Bharu Plaza #B1-125',
      'Raffles City #B1-01'
    ]
  },
  {
    location: 'Guardian - North East',
    stores: [
      'Giant Tampines Retail Park #01-01',
      'Pasir Ris Mall #B1-29',
      'Century Square #B1-01 '
    ]
  },
  {
    location: 'Guardian - North West',
    stores: [
      'Causeway Point #B1-28/29/30',
      'Bukit Panjang Plaza #01-01/02/03',
      'NorthPoint (NorthWing) #B1-02/03'
    ]
  },
  {
    location: 'Guardian - South East',
    stores: [
      'Kallang Leisure Park #01-23',
      'Kallang Wave Mall #01-51',
      'One KM #B1-19/20',
      'Parkway Parade #B1-147/149',
      'NEX #B1-62/63'
    ]
  },
  {
    location: 'Guardian - South West',
    stores: [
      'Harbourfront Centre #02-111/112',
      'Lot 1 #B1-26/27',
      'JEM #B1-16/17',
      'Westgate #B1-20',
      'Jurong Point #B1-27/28',
      'VIVO City #02-79',
      'Le Quest #01-58/59/60',
      'IMM Building #01-99'
    ]
  }
]

const OutletsLocation = ({ outlet }: { outlet: Outlets }) => {
  return (
    <Box data-aos="fade-up" my={10}>
      {outlet.location && (
        <Text textStyle="supplementExclusiveOfferTitle" w={'100%'}>
          {outlet.location}
        </Text>
      )}

      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={6} w={'100%'} mt={5}>
        {outlet.stores.map((store) => {
          return (
            <HStack key={store}>
              <Box
                minW={{
                  base: '20px',
                  lg: '28px'
                }}
                w={{
                  base: '20px',
                  lg: '28px'
                }}
                h={{
                  base: '20px',
                  lg: '28px'
                }}
              >
                <LocationIcon width="100%" height="100%" />
              </Box>
              <Text textStyle="supplementExclusiveOffer">{store}</Text>
            </HStack>
          )
        })}
      </SimpleGrid>
    </Box>
  )
}

const ExclusiveOffer = () => {
  const { lgMin: isAboveLg } = useResponsive()
  return (
    <Box w="100%" position="relative" mt={20} mb={12}>
      <MainContainer>
        <Box
          maxW="800px"
          mx="auto"
          mb={12}
          pb={{
            base: '80px',
            sm: 140
          }}
        >
          <VStack data-aos="fade-up" alignItems={'center'} gap={12}>
            <VStack data-aos="fade-up" alignItems={'center'} gap={1}>
              <Text textAlign={'center'} textStyle={'exclusiveTitle'}>
                Participating outlets
              </Text>
              <Image
                alt=""
                src={
                  'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/triangle-1731460698402.png'
                }
                width={580}
                height={110}
              />
            </VStack>
          </VStack>
          <Flex data-aos="fade-up" my={8} justifyContent="center">
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              justify="center" //
              align={{ base: 'center', sm: 'center' }}
              gap={3}
              w="100%"
              maxW={620}
              mt={5}
            >
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
              ></Box>
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
              >
                <AspectRatio ratio={5 / 3} position="relative" cursor="pointer">
                  <ScrollLink
                    to="guardian"
                    smooth={true}
                    duration={500}
                    offset={isAboveLg ? -120 : -120} // Adjusted for header height
                  >
                    <Image
                      src={
                        'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/guardient-1731560278894.png'
                      }
                      alt="guardian"
                      fill
                    />
                  </ScrollLink>
                </AspectRatio>
              </Box>
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
              >
                <AspectRatio ratio={5 / 3} position="relative" cursor="pointer">
                  <ScrollLink
                    to="og"
                    smooth={true}
                    duration={500}
                    offset={isAboveLg ? -100 : -120} // Adjusted for header height
                  >
                    <Image
                      src={
                        'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/og-logo-1731462041350.png'
                      }
                      alt="og"
                      fill
                    />
                  </ScrollLink>
                </AspectRatio>
              </Box>
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
              ></Box>
            </Flex>
          </Flex>
          <Flex
            data-aos="fade-up"
            my={8}
            id="jml"
            position={'relative'}
            justifyContent="center"
          >
            <Box
              position={'absolute'}
              display={{
                base: 'none',
                sm: 'block'
              }}
              bottom={{
                base: '97%',
                sm: '100%',
                md: '100%',
                lg: '50%'
              }}
              left={{
                base: '-10px',
                sm: 0,
                md: '-40px',
                lg: '-100px'
              }}
            >
              <AspectRatio
                ratio={1366 / 768}
                cursor="pointer"
                w={{
                  base: 120,
                  lg: 160
                }}
              >
                <Image
                  src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/click-to-visit-1731462322641.png"
                  fill
                  alt="wonder"
                  style={{
                    borderRadius: '18px'
                  }}
                />
              </AspectRatio>
            </Box>
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              justify="space-between" //
              align={{ base: 'center', sm: 'center' }}
              gap={3}
              w="100%"
              maxW={620}
              mt={5}
            >
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
              >
                <Link href="https://shp.ee/qxarhnv" target="_blank">
                  <AspectRatio ratio={5 / 3} cursor="pointer">
                    <Image
                      src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/shopee-logo-1731462041350.png"
                      fill
                      alt="wonder"
                      style={{
                        borderRadius: '18px'
                      }}
                    />
                  </AspectRatio>
                </Link>
              </Box>
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
                cursor="pointer"
              >
                <Link
                  href="https://www.lazada.sg/shop/jml/custom-1733372179552.htm?wh_weex=true"
                  target="_blank"
                >
                  <AspectRatio ratio={5 / 3} position="relative">
                    <Image
                      src={
                        'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/lazada-1731462041349.png'
                      }
                      alt="cheers"
                      fill
                    />
                  </AspectRatio>
                </Link>
              </Box>
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
                cursor="pointer"
                shadow={'md'}
                borderRadius={'16px'}
              >
                <Link href="https://habbit.sg/" target="_blank">
                  <AspectRatio ratio={5 / 3} position="relative">
                    <Image
                      src={
                        'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/habbit-1731462041348.png'
                      }
                      alt="shell select"
                      fill
                    />
                  </AspectRatio>
                </Link>
              </Box>
              <Box
                flex={1}
                w={{
                  base: '60%',
                  sm: 'auto'
                }}
                cursor="pointer"
              >
                <Link href="https://www.jml.sg/sg/" target="_blank">
                  <AspectRatio ratio={5 / 3} position="relative">
                    <Image
                      src={
                        'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/jml-logo-1731462041349.png'
                      }
                      alt="guardian"
                      fill
                    />
                  </AspectRatio>
                </Link>
              </Box>
            </Flex>
          </Flex>
          <VStack mt={20} w={'100%'} alignItems={'start'}>
            <Text textAlign={'left'} textStyle={'supplementParticipateTitle'}>
              JML Experience Store
            </Text>
          </VStack>

          {JMLOutlets.map((outlet, i) => {
            return <OutletsLocation key={i} outlet={outlet} />
          })}
          <VStack mt={20} w={'100%'} alignItems={'start'} id="guardian">
            <Text textAlign={'left'} textStyle={'supplementParticipateTitle'}>
              Guardian
            </Text>
          </VStack>
          {GuardianOutlets.map((outlet, i) => {
            return <OutletsLocation key={i} outlet={outlet} />
          })}

          <VStack mt={20} w={'100%'} alignItems={'start'} id="og">
            <Text textAlign={'left'} textStyle={'supplementParticipateTitle'}>
              OG
            </Text>
          </VStack>
          {OGOutlets.map((outlet, i) => {
            return <OutletsLocation key={i} outlet={outlet} />
          })}
        </Box>
      </MainContainer>

      <Box position={'absolute'} bottom={0} left={0} right={0}>
        <AspectRatio ratio={2740 / 158} w={'100%'} position="relative">
          <Image
            src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-body-bot-1730953433163.png"
            alt="bg bot item"
            fill
            className="object-cover"
          />
        </AspectRatio>

        <Flex justifyContent={'center'} mt={10}>
          <AspectRatio ratio={280 / 32} w={280} position="relative">
            <Image
              src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/text-bot-1731462953916.png"
              alt="bg bot item"
              fill
              className="object-cover"
            />
          </AspectRatio>
        </Flex>
      </Box>
    </Box>
  )
}

export default ExclusiveOffer
